// src/components/partners/SupplierList.jsx
import React, { useState, useEffect } from 'react';
import { Table, Button, Card, Row, Col, Form, InputGroup, Alert } from 'react-bootstrap';
import { Search, Edit, Badge, RefreshCw, XCircle, Info } from 'lucide-react';
import useAuth from '../../hooks/useAuth';
import { deleteMultipleSuppliers, deleteSupplier, getAllSuppliers } from '../../api/partner';

const SupplierList = ({ onAddSupplier, onEditSupplier, onViewSupplier }) => {
    const { isAdmin, isManager } = useAuth();
    const [suppliers, setSuppliers] = useState([]);
    const [filteredSuppliers, setFilteredSuppliers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [selectedSuppliers, setSelectedSuppliers] = useState([]);
    const [selectAll, setSelectAll] = useState(false);

    const canModify = isAdmin || isManager;

    useEffect(() => {
        fetchSuppliers();
    }, [refreshTrigger]);

    useEffect(() => {
        filterSuppliers();
    }, [searchTerm, suppliers]);

    useEffect(() => {
        // Réinitialiser la sélection quand les fournisseurs filtrés changent
        setSelectedSuppliers([]);
        setSelectAll(false);
    }, [filteredSuppliers]);

    const fetchSuppliers = async () => {
        try {
            setLoading(true);
            setError('');
            const data = await getAllSuppliers();
            setSuppliers(data);
            setFilteredSuppliers(data);
        } catch (err) {
            setError('Impossible de charger les clients. Veuillez réessayer.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    console.log('data ', suppliers)
    const filterSuppliers = () => {
        if (searchTerm.trim() === '') {
            setFilteredSuppliers(suppliers);
        } else {
            const filtered = suppliers.filter(supplier =>
                supplier.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (supplier.email && supplier.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (supplier.contactPerson && supplier.contactPerson.toLowerCase().includes(searchTerm.toLowerCase()))
            );
            setFilteredSuppliers(filtered);
        }
    };

    const handleRefresh = () => {
        setRefreshTrigger(prev => prev + 1);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer ce fournisseur ?')) {
            try {
                await deleteSupplier(id);
                setSuppliers(suppliers.filter(supplier => supplier.id !== id));
            } catch (err) {
                setError('Échec de la suppression du fournisseur. Veuillez réessayer.');
                console.error(err);
            }
        }
    };

    const handleSelectAll = (e) => {
        const isChecked = e.target.checked;
        setSelectAll(isChecked);
        setSelectedSuppliers(isChecked ? filteredSuppliers.map(supplier => supplier.id) : []);
    };

    const handleSelectSupplier = (id) => {
        setSelectedSuppliers(prev => {
            if (prev.includes(id)) {
                return prev.filter(supplierId => supplierId !== id);
            } else {
                return [...prev, id];
            }
        });
    };

    const handleDeleteSelected = async () => {
        if (selectedSuppliers.length === 0) return;

        if (window.confirm(`Êtes-vous sûr de vouloir supprimer les ${selectedSuppliers.length} fournisseurs sélectionnés ?`)) {
            try {
                await deleteMultipleSuppliers(selectedSuppliers);
                setSuppliers(suppliers.filter(supplier => !selectedSuppliers.includes(supplier.id)));
                setSelectedSuppliers([]);
                setSelectAll(false);
            } catch (err) {
                setError('Échec de la suppression des fournisseurs sélectionnés. Veuillez réessayer.');
                console.error(err);
            }
        }
    };

    return (
        <Card className="shadow-sm">
            <Card.Header>
                <Row className="align-items-center">
                    <Col>
                        <h5 className="mb-0">Liste des clients</h5>
                    </Col>
                    <Col xs="auto">
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={handleRefresh}
                            title="Rafraîchir"
                        >
                            <RefreshCw size={16} />
                        </Button>
                    </Col>
                </Row>
            </Card.Header>
            <Card.Body>
                {error && <Alert variant="danger">{error}</Alert>}

                <Row className="mb-3">
                    <Col md={8}>
                        <InputGroup>
                            <InputGroup.Text>
                                <Search size={18} />
                            </InputGroup.Text>
                            <Form.Control
                                placeholder="Rechercher par nom, email ou personne de contact..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </InputGroup>
                    </Col>
                    <Col md={4} className="mt-2 mt-md-0">
                        {canModify && selectedSuppliers.length > 0 && (
                            <Button
                                variant="danger"
                                onClick={handleDeleteSelected}
                                className="w-100"
                            >
                                <XCircle size={16} className="me-1" />
                                Supprimer sélectionnés ({selectedSuppliers.length})
                            </Button>
                        )}
                    </Col>
                </Row>

                {loading ? (
                    <div className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Chargement...</span>
                        </div>
                        <p className="mt-2">Chargement des fournisseurs...</p>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <Table striped hover>
                            <thead>
                                <tr>
                                    {canModify && (
                                        <th width="50">
                                            <Form.Check
                                                type="checkbox"
                                                checked={selectAll}
                                                onChange={handleSelectAll}
                                            />
                                        </th>
                                    )}
                                    <th>Nom</th>
                                    <th>Email</th>
                                    <th>Téléphone</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredSuppliers.length === 0 ? (
                                    <tr>
                                        <td colSpan={canModify ? 6 : 5} className="text-center py-3">
                                            Aucun client trouvé
                                        </td>
                                    </tr>
                                ) : (
                                    filteredSuppliers.map(supplier => (
                                        <tr key={supplier.id}>
                                            {canModify && (
                                                <td>
                                                    <Form.Check
                                                        type="checkbox"
                                                        checked={selectedSuppliers.includes(supplier.id)}
                                                        onChange={() => handleSelectSupplier(supplier.id)}
                                                    />
                                                </td>
                                            )}
                                            <td>{supplier.name}</td>
                                            <td>{supplier.email || '-'}</td>
                                            <td>{supplier.phone || '-'}</td>
                                            {/* <td>
                                            {supplier.vatApplicable ? (
                                                <Badge bg="success">Oui</Badge>
                                            ) : (
                                                <Badge bg="secondary">Non</Badge>
                                            )}
                                            </td> */}
                                            <td>
                                                <Button
                                                    variant="outline-info"
                                                    size="sm"
                                                    className="me-1"
                                                    onClick={() => onViewSupplier(supplier.id)}
                                                >
                                                    <Info size={16} />
                                                </Button>
                                                {canModify && (
                                                    <>
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            className="me-1"
                                                            onClick={() => onEditSupplier(supplier.id)}
                                                        >
                                                            <Edit size={16} />
                                                        </Button>
                                                        {/* <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            onClick={() => handleDelete(supplier.id)}
                                                        >
                                                            <Trash size={16} />
                                                        </Button> */}
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </div>
                )}
            </Card.Body>
        </Card>
    );
};

export default SupplierList;